import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { db, storage, auth } from "../firebase";
import { createUserWithEmailAndPassword, signOut } from "firebase/auth";
import {
  doc,
  setDoc,
  updateDoc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import AuthShell from "../components/AuthShell";
import { showToast } from "../utils/toast";
import { getAuthErrorMessage } from "../utils/authErrors";

const fieldClassName =
  "w-full rounded-2xl border border-[#ead7b0] bg-white/90 px-4 py-3 text-[#4e3520] shadow-[0_12px_30px_-28px_rgba(69,39,15,0.45)] outline-none transition placeholder:text-[#b39368] focus:border-[#c99649] focus:ring-2 focus:ring-[#efd8aa]";

const uploadFieldClassName =
  "block w-full rounded-2xl border border-[#ead7b0] bg-white/90 px-4 py-3 text-sm text-[#6c4d2c] shadow-[0_12px_30px_-28px_rgba(69,39,15,0.45)] file:mr-4 file:rounded-xl file:border-0 file:bg-[#fff1d6] file:px-4 file:py-2 file:font-semibold file:text-[#8a5a1d] hover:file:bg-[#f9e4b5]";

const primaryButtonClassName =
  "w-full rounded-2xl bg-[linear-gradient(135deg,#a56a22,#d6a654)] px-4 py-3 text-sm font-semibold text-white shadow-[0_20px_40px_-24px_rgba(118,73,20,0.65)] transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-60";

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const Register = () => {
  const navigate = useNavigate();
  const search = useLocation().search;
  const referral = new URLSearchParams(search).get("ref") || "";

  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    whatsapp: "",
    nic: "",
    password: "",
    confirmPassword: "",
    profilePic: null,
    nicFrontPic: null,
    nicBackPic: null,
    bankName: "",
    bankBranch: "",
    bankAccount: "",
    accountHolder: "",
    bankFrontImg: null,
  });
  const [preview, setPreview] = useState(null);
  const [nicFrontPreview, setNicFrontPreview] = useState(null);
  const [nicBackPreview, setNicBackPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "profilePic") {
      setForm({ ...form, profilePic: files[0] });
      setPreview(URL.createObjectURL(files[0]));
    } else if (name === "nicFrontPic") {
      setForm({ ...form, nicFrontPic: files[0] });
      setNicFrontPreview(URL.createObjectURL(files[0]));
    } else if (name === "nicBackPic") {
      setForm({ ...form, nicBackPic: files[0] });
      setNicBackPreview(URL.createObjectURL(files[0]));
    } else if (name === "bankFrontImg") {
      setForm({ ...form, bankFrontImg: files[0] });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password !== form.confirmPassword) {
      setError("Passwords do not match!");
      return;
    }
    if (
      !form.fullName ||
      !form.email ||
      !form.phone ||
      !form.whatsapp ||
      !form.password
    ) {
      setError("Please fill in the required account details.");
      return;
    }
    setLoading(true);

    try {
      let userLevel = 1;
      const publicReferralSnap = await getDoc(doc(db, "referral_links", referral));
      const codeRef = doc(db, "referral_codes", referral);
      const codeSnap = await getDoc(codeRef);

      if (publicReferralSnap.exists()) {
        const referralData = publicReferralSnap.data() || {};
        const inviterLevel = Number(referralData.level || 1) || 1;
        const referralStatus = String(referralData.status || "").toLowerCase();

        if (referralStatus && referralStatus !== "active") {
          setError("This referral link is not active yet.");
          setLoading(false);
          return;
        }

        if (inviterLevel >= 3) {
          setError(
            "Cannot register with a Level 3 referral code. Max depth reached."
          );
          setLoading(false);
          return;
        }

        userLevel = inviterLevel + 1;
      } else if (
        codeSnap.exists() &&
        Number(codeSnap.data().level || 0) === 1 &&
        codeSnap.data().enabled
      ) {
        userLevel = 1;
      } else {
        setError("Invalid referral code.");
        setLoading(false);
        return;
      }

      const userCred = await createUserWithEmailAndPassword(
        auth,
        form.email.trim(),
        form.password
      );
      const uid = userCred.user.uid;

      await setDoc(
        doc(db, "referral_users", uid),
        {
          uid,
          fullName: form.fullName,
          email: form.email,
          phone: form.phone,
          whatsapp: form.whatsapp,
          nic: form.nic,
          bankName: form.bankName,
          bankBranch: form.bankBranch,
          bankAccount: form.bankAccount,
          accountHolder: form.accountHolder,
          referralCode: generateReferralCode(form.fullName, uid),
          referredBy: referral,
          createdAt: serverTimestamp(),
          role: "referral",
          status: "pending",
          level: userLevel,
          registrationStage: "uploading_kyc",
        },
        { merge: true }
      );

      await userCred.user.getIdToken(true);
      await wait(250);

      const uploadImg = async (file, path) => {
        if (!file) return "";
        const imgRef = ref(storage, path);
        await uploadBytes(imgRef, file);
        return await getDownloadURL(imgRef);
      };

      const imageUrl = await uploadImg(form.profilePic, `profile_images/${uid}`);
      const nicFrontUrl = await uploadImg(
        form.nicFrontPic,
        `nic_front_images/${uid}`
      );
      const nicBackUrl = await uploadImg(
        form.nicBackPic,
        `nic_back_images/${uid}`
      );
      const bankFrontUrl = await uploadImg(
        form.bankFrontImg,
        `bank_front_images/${uid}`
      );

      await updateDoc(doc(db, "referral_users", uid), {
        imageUrl,
        nicFrontUrl,
        nicBackUrl,
        bankFrontUrl,
        registrationStage: "complete",
      });

      await signOut(auth);
      setLoading(false);
      showToast("Registration successful! You can now login.", {
        type: "success",
        duration: 3600,
      });
      navigate("/login");
    } catch (err) {
      setError(getAuthErrorMessage(err, "register"));
      setLoading(false);
    }
  };

  function generateReferralCode(name, uid) {
    return (name.split(" ")[0] + uid.slice(-4)).toUpperCase();
  }

  if (!referral) {
    return (
      <AuthShell
        portalLabel="Referral Portal"
        badge="Invite Only"
        title="Registration needs a valid referral link"
        description="Referral accounts can only be created from an approved invite link so the network structure stays accurate."
      >
        <div className="mx-auto w-full max-w-md space-y-4 text-center">
          <div className="rounded-2xl border border-[#f0dfc0] bg-[#fff9ef] px-5 py-5 text-sm leading-7 text-[#6c4d2c]">
            Please ask your referrer to send you their invite link, then open
            that link again to continue registration.
          </div>
          <button
            className={primaryButtonClassName}
            onClick={() => navigate("/login")}
          >
            Go to Login
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      portalLabel="Referral Portal"
      badge="Create Your Account"
      title="Join the referral network"
      description="Create your referral account with the same premium Ceylon Web Mall auth experience used across the other portals."
      footer={
        <>
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-semibold text-[#9b6b2f] transition hover:text-[#7e541f]"
          >
            Login
          </Link>
        </>
      }
    >
      <div className="mx-auto w-full max-w-lg">
        {referral && (
          <div className="mb-4 rounded-2xl border border-[#c8ddb3] bg-[#f4faee] px-4 py-3 text-center text-sm font-semibold text-[#527239]">
            Referred by: <span className="font-mono">{referral}</span>
          </div>
        )}
        {error && (
          <div className="mb-4 rounded-2xl border border-[#e7b0ab] bg-[#fff4f3] px-4 py-3 text-sm text-[#9f3b2f]">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="fullName"
              className="mb-2 block text-sm font-semibold text-[#6c4d2c]"
            >
              Full Name
            </label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              placeholder="Full Name"
              className={fieldClassName}
              value={form.fullName}
              onChange={handleChange}
              required
            />
          </div>

          <div>
            <label
              htmlFor="email"
              className="mb-2 block text-sm font-semibold text-[#6c4d2c]"
            >
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              placeholder="you@example.com"
              className={fieldClassName}
              value={form.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label
                htmlFor="phone"
                className="mb-2 block text-sm font-semibold text-[#6c4d2c]"
              >
                Phone
              </label>
              <input
                type="text"
                id="phone"
                name="phone"
                placeholder="Phone"
                className={fieldClassName}
                value={form.phone}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label
                htmlFor="whatsapp"
                className="mb-2 block text-sm font-semibold text-[#6c4d2c]"
              >
                WhatsApp
              </label>
              <input
                type="text"
                id="whatsapp"
                name="whatsapp"
                placeholder="WhatsApp Number"
                className={fieldClassName}
                value={form.whatsapp}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label
                htmlFor="nic"
                className="mb-2 block text-sm font-semibold text-[#6c4d2c]"
              >
                NIC (Optional)
              </label>
              <input
                type="text"
                id="nic"
                name="nic"
                placeholder="NIC"
                className={fieldClassName}
                value={form.nic}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="password"
                className="mb-2 block text-sm font-semibold text-[#6c4d2c]"
              >
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                placeholder="********"
                className={fieldClassName}
                value={form.password}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label
                htmlFor="confirmPassword"
                className="mb-2 block text-sm font-semibold text-[#6c4d2c]"
              >
                Confirm Password
              </label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                placeholder="********"
                className={fieldClassName}
                value={form.confirmPassword}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="sm:col-span-1">
              <label className="mb-2 block text-sm font-semibold text-[#6c4d2c]">
                Profile Picture (Optional)
              </label>
              <input
                type="file"
                name="profilePic"
                accept="image/*"
                onChange={handleChange}
                className={uploadFieldClassName}
              />
              {preview && (
                <img
                  src={preview}
                  alt="Preview"
                  className="mt-3 h-16 w-16 rounded-2xl border border-[#ead7b0] object-cover"
                />
              )}
            </div>

            <div className="sm:col-span-1">
              <label className="mb-2 block text-sm font-semibold text-[#6c4d2c]">
                NIC Front (Optional)
              </label>
              <input
                type="file"
                name="nicFrontPic"
                accept="image/*"
                onChange={handleChange}
                className={uploadFieldClassName}
              />
              {nicFrontPreview && (
                <img
                  src={nicFrontPreview}
                  alt="NIC Front Preview"
                  className="mt-3 h-16 w-24 rounded-2xl border border-[#ead7b0] object-cover"
                />
              )}
            </div>

            <div className="sm:col-span-1">
              <label className="mb-2 block text-sm font-semibold text-[#6c4d2c]">
                NIC Back (Optional)
              </label>
              <input
                type="file"
                name="nicBackPic"
                accept="image/*"
                onChange={handleChange}
                className={uploadFieldClassName}
              />
              {nicBackPreview && (
                <img
                  src={nicBackPreview}
                  alt="NIC Back Preview"
                  className="mt-3 h-16 w-24 rounded-2xl border border-[#ead7b0] object-cover"
                />
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-[#f0dfc0] bg-[#fff9ef] px-4 py-3 text-sm leading-6 text-[#7d5a2a]">
            NIC details and document uploads are optional at registration. You
            can add or update them later from your profile page.
          </div>

          <button
            type="submit"
            disabled={loading}
            className={primaryButtonClassName}
          >
            {loading ? "Registering..." : "Register"}
          </button>
        </form>
      </div>
    </AuthShell>
  );
};

export default Register;
